import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { ApiCallError, callFunction } from '@/lib/api';
import { answersLabel } from '@/lib/referral/receivedEpikrise';
import { listReceivedMessages } from '@/lib/referral/receivedList';
import { BOOKINGSVAR_HEADING, BOOKINGSVAR_RETTET_BANNER, bookingDateTime, dtText } from '@/lib/referral/receivedBookingsvar';
import { AttachPatient, type ReceivedRow } from './ReceivedEpikriserPanel';
import { PartyIdentifier } from './PartyIdentifier';
import { ReceivedMessageCpr } from './ReceivedMessageCpr';
import { formatDanishDateTime, formatDanishTime } from '@/lib/danishDateTime';

// Spor A T1.6 (DIS13-M): "Modtagne bookingsvar" (XDIS13). The inbox shows
// patient, sender, booking date/time and text; a click opens the bookingsvar in
// the order DIS13-M 3.2–3.8 asks for. communicate-epikriser keeps every CPR
// server-side; a bookingsvar is coupled to our sent referral only by its
// EpisodeOfCareIdentifier plus the same patient.

type Row = ReceivedRow & { confirmation_at: string | null; booking_text: string | null; referral_received_at: string | null; episode_of_care_identifier: string | null; referral_identifier: string | null };

type Party = {
  ean?: string;
  identifier?: string;
  identifierCode?: string;
  organisationName?: string;
  departmentName?: string;
  unitName?: string;
  streetName?: string;
  suburbName?: string;
  districtName?: string;
  postCodeIdentifier?: string;
  telephoneSubscriberIdentifier?: string;
} | null;
type Dt = { date: string; time: string | null } | null;
type Linked = { id: string; exchange_id: string; received_at: string | null; letter_identifier: string | null } | null;

export type BookingsvarDetail = {
  row: { id: string; exchange_id: string; received_at: string | null; xsd_ok: boolean | null; patient_id: string | null; match_status: string };
  document: {
    kind: string;
    statusCode: string;
    letterIdentifier: string;
    sentDate: string;
    sentTime: string;
    episodeOfCareIdentifier: string | null;
    sender: Party;
    senderContact: Party;
    receiver: Party;
    patient: {
      idKind: 'cpr' | 'erstatnings_cpr' | 'none';
      personSurnameName: string;
      personGivenName: string | null;
      streetName: string | null;
      suburbName: string | null;
      districtName: string | null;
      postCodeIdentifier: string | null;
      occupancyText: string | null;
    };
    referral: { identifier: string | null; received: Dt } | null;
    confirmation: Dt;
    text: string | null;
  };
  corrects: Linked;
  corrected_by: Linked;
  answers: { id: string; letter_identifier: string; created_at: string; kind: string } | null;
};

const AUTO_REFRESH_MS = 30_000;

function formatTime(iso: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? iso
    : formatDanishDateTime(date);
}

function partyLines(p: Party): string[] {
  if (!p) return [];
  const address = [p.streetName, [p.postCodeIdentifier, p.districtName].filter(Boolean).join(' '), p.suburbName]
    .filter((v) => v && v.trim().length > 0)
    .join(', ');
  return [p.organisationName, p.departmentName, p.unitName, address, p.telephoneSubscriberIdentifier ? `Tlf. ${p.telephoneSubscriberIdentifier}` : null]
    .filter((v): v is string => Boolean(v && v.trim()));
}

function Field({ label, children, testId }: { label: string; children: React.ReactNode; testId?: string }) {
  return (
    <div className="grid grid-cols-[12rem_1fr] gap-2 text-sm" data-testid={testId}>
      <dt className="font-medium text-ds-muted">{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

export function ReceivedBookingsvarDetail({
  clinicId,
  detail,
  onOpenExchange,
}: {
  clinicId: string;
  detail: BookingsvarDetail;
  onOpenExchange: (exchangeId: string) => void;
}) {
  const { t } = useTranslation();
  const doc = detail.document;
  const rettet = doc.statusCode === 'rettetbrev';
  const patientName = [doc.patient.personGivenName, doc.patient.personSurnameName].filter(Boolean).join(' ');
  const patientAddress = [doc.patient.streetName, [doc.patient.postCodeIdentifier, doc.patient.districtName].filter(Boolean).join(' '), doc.patient.suburbName]
    .filter((v) => v && v.trim().length > 0)
    .join(', ');
  const contact = doc.senderContact;
  return (
    <article className="space-y-3 rounded-md border border-ds-line p-4" data-testid="bookingsvar-detail">
      {rettet && (
        <div className="rounded-md bg-ds-red-tint px-3 py-2 text-sm font-bold uppercase tracking-wide text-ds-red-ink" data-testid="bookingsvar-rettet-banner">
          {BOOKINGSVAR_RETTET_BANNER}
          {detail.corrects && (
            <button type="button" className="ml-2 underline" onClick={() => onOpenExchange(detail.corrects!.exchange_id)}>
              {t('medcom.bookingsvar.open_replaced', 'Vis det erstattede bookingsvar')}
            </button>
          )}
        </div>
      )}
      {detail.corrected_by && (
        <div className="rounded-md bg-ds-amber-tint px-3 py-2 text-sm text-ds-amber-ink">
          {t('medcom.bookingsvar.replaced_by', 'Dette bookingsvar er erstattet af et rettet bookingsvar.')}{' '}
          <button type="button" className="underline" onClick={() => onOpenExchange(detail.corrected_by!.exchange_id)}>
            {t('medcom.bookingsvar.open_rettet', 'Vis det rettede')}
          </button>
        </div>
      )}
      <h4 className="text-base font-semibold">{BOOKINGSVAR_HEADING}</h4>
      <dl className="space-y-1.5">
        <Field label={t('medcom.bookingsvar.sender', 'Afsender')} testId="bookingsvar-sender">
          {partyLines(doc.sender).map((l) => <div key={l}>{l}</div>)}
          {(doc.sender?.ean || doc.sender?.identifier) && (
            <div className="text-xs text-ds-muted">
              {doc.sender.ean && `EAN ${doc.sender.ean}`}
              <PartyIdentifier party={doc.sender} />
            </div>
          )}
        </Field>
        <Field label={t('medcom.bookingsvar.contact', 'Kontaktperson')} testId="bookingsvar-contact">
          {contact ? [contact.departmentName, contact.unitName].filter(Boolean).join(' ') || '–' : '–'}
        </Field>
        <Field label={t('medcom.bookingsvar.receiver', 'Modtager')} testId="bookingsvar-receiver">
          {partyLines(doc.receiver).map((l) => <div key={l}>{l}</div>)}
          {(doc.receiver?.ean || doc.receiver?.identifier) && (
            <div className="text-xs text-ds-muted">
              {doc.receiver.ean && `EAN ${doc.receiver.ean}`}
              <PartyIdentifier party={doc.receiver} />
            </div>
          )}
        </Field>
        <Field label={t('medcom.bookingsvar.patient', 'Patient')} testId="bookingsvar-patient">
          <div>{patientName || '–'}</div>
          {patientAddress && <div>{patientAddress}</div>}
        </Field>
        <Field
          label={
            doc.patient.idKind === 'erstatnings_cpr'
              ? t('referrals.replacement_cpr', 'Erstatnings-CPR')
              : t('medcom.epikriser.cpr', 'CPR-nummer')
          }
          testId="bookingsvar-patient-cpr"
        >
          <ReceivedMessageCpr clinicId={clinicId} exchangeId={detail.row.exchange_id} idKind={doc.patient.idKind} />
        </Field>
        <Field label={t('medcom.bookingsvar.occupancy', 'Stilling')} testId="bookingsvar-occupancy">
          {doc.patient.occupancyText ?? '–'}
        </Field>
        <Field label={t('medcom.bookingsvar.confirmation', 'Dato og tid for booking')} testId="bookingsvar-confirmation">
          <span className="font-semibold">{bookingDateTime(dtText(doc.confirmation)) || '–'}</span>
        </Field>
        <Field label={t('medcom.bookingsvar.text', 'Bookingtekst')} testId="bookingsvar-text">
          {doc.text ?? '–'}
        </Field>
        <Field label={t('medcom.bookingsvar.referral_date', 'Henvisningsdato')} testId="bookingsvar-referral-date">
          {bookingDateTime(dtText(doc.referral?.received ?? null)) || '–'}
        </Field>
        <Field label={t('medcom.bookingsvar.referral_id', 'Henvisningens brev-id')} testId="bookingsvar-referral-id">
          {doc.referral?.identifier ?? '–'}
        </Field>
        <Field label="UUID" testId="bookingsvar-uuid">
          <span className="font-mono text-xs">{doc.episodeOfCareIdentifier ?? '–'}</span>
        </Field>
        <Field label={t('medcom.bookingsvar.answers', 'Svar på vores henvisning')} testId="bookingsvar-answers">
          {detail.answers
            ? answersLabel(detail.answers.letter_identifier, detail.answers.created_at)
            : t('medcom.bookingsvar.no_answer_link', 'Ikke parret med en henvisning sendt fra Clinio til denne patient')}
        </Field>
        <Field label={t('medcom.bookingsvar.letter', 'Brev-id / sendt')}>
          {doc.letterIdentifier} · {bookingDateTime(`${doc.sentDate} ${doc.sentTime}`.trim())}
        </Field>
        <Field label={t('medcom.bookingsvar.received', 'Modtaget')}>
          {formatTime(detail.row.received_at)}
          {detail.row.xsd_ok === false && (
            <span className="ml-2 text-xs text-ds-amber-ink">{t('medcom.bookingsvar.xsd_warning', 'XSD-tjek: afvigelse (kun information)')}</span>
          )}
        </Field>
      </dl>
    </article>
  );
}

export function ReceivedBookingsvarPanel({
  clinicId,
  patientId,
  refreshKey,
  canAttach = false,
}: {
  clinicId: string;
  patientId?: string;
  refreshKey?: number;
  canAttach?: boolean;
}) {
  const { t } = useTranslation();
  const [rows, setRows] = useState<Row[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadedAt, setLoadedAt] = useState<Date | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [detail, setDetail] = useState<BookingsvarDetail | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [detailBusy, setDetailBusy] = useState(false);
  const seq = useRef(0);

  const load = useCallback(async () => {
    const mine = ++seq.current;
    setLoading(true);
    setError(null);
    try {
      const result = await listReceivedMessages<Row>(clinicId, patientId);
      if (mine !== seq.current) return;
      setRows(result.epikriser.filter((r) => r.kind === 'XDIS13'));
      setLoadedAt(new Date());
    } catch (err) {
      if (mine !== seq.current) return;
      setError(err instanceof ApiCallError ? err.message : t('medcom.bookingsvar.load_failed', 'Bookingsvar kunne ikke hentes.'));
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, [clinicId, patientId, t]);

  useEffect(() => {
    setRows(null);
    setOpenId(null);
    setDetail(null);
  }, [clinicId, patientId]);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible' && !openId) void load();
    }, AUTO_REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [load, openId]);

  const openDetail = useCallback(
    async (exchangeId: string) => {
      setOpenId(exchangeId);
      setDetail(null);
      setDetailError(null);
      setDetailBusy(true);
      try {
        setDetail(
          await callFunction<Record<string, string>, BookingsvarDetail>('communicate-epikriser', {
            action: 'detail',
            clinic_id: clinicId,
            exchange_id: exchangeId,
          }),
        );
      } catch (err) {
        setDetailError(err instanceof ApiCallError ? err.message : t('medcom.bookingsvar.detail_failed', 'Bookingsvaret kunne ikke vises.'));
      } finally {
        setDetailBusy(false);
      }
    },
    [clinicId, t],
  );

  const openRow = rows?.find((r) => r.exchange_id === openId) ?? null;

  return (
    <section className="space-y-3 rounded-md border border-ds-line p-4" data-testid="received-bookingsvar">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">{t('medcom.bookingsvar.title', 'Modtagne bookingsvar')} (XDIS13)</h3>
        <div className="flex items-center gap-2">
          {loadedAt && (
            <span className="text-xs text-ds-muted">
              {t('medcom.bookingsvar.loaded_at', 'Opdateret')} {formatDanishTime(loadedAt)}
            </span>
          )}
          <Button type="button" variant="secondary" size="sm" onClick={() => void load()} disabled={loading}>
            {loading ? t('medcom.bookingsvar.refreshing', 'Opdaterer…') : t('medcom.bookingsvar.refresh', 'Opdatér')}
          </Button>
        </div>
      </div>
      {error && <p role="alert" className="text-sm text-ds-red-ink">{error}</p>}
      {rows === null && loading && (
        <div className="flex flex-col gap-s3" aria-busy>
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      )}
      {rows && rows.length === 0 && <p className="text-sm text-ds-muted">{t('medcom.bookingsvar.empty', 'Ingen modtagne bookingsvar.')}</p>}
      {rows && rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm" data-testid="received-bookingsvar-inbox">
            <thead className="text-xs text-ds-muted">
              <tr>
                <th className="py-1 pr-2">{t('medcom.bookingsvar.col_type', 'Type')}</th>
                <th className="py-1 pr-2">{t('medcom.bookingsvar.col_patient', 'Patient')}</th>
                <th className="py-1 pr-2">{t('medcom.bookingsvar.col_sender', 'Afsender')}</th>
                <th className="py-1 pr-2">{t('medcom.bookingsvar.col_booking', 'Booking')}</th>
                <th className="py-1 pr-2">{t('medcom.bookingsvar.col_text', 'Bookingtekst')}</th>
                <th className="py-1">{t('medcom.bookingsvar.col_date', 'Modtaget')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.id}
                  className={`cursor-pointer border-t border-ds-line align-top hover:bg-ds-row-hover   ${openId === row.exchange_id ? 'bg-ds-teal-tint ' : ''}`}
                  onClick={() => void openDetail(row.exchange_id)}
                  data-testid="received-bookingsvar-row"
                >
                  <td className="py-1.5 pr-2">
                    <span className="font-medium">{BOOKINGSVAR_HEADING}</span>
                    {row.status_code === 'rettetbrev' && (
                      <span className="ml-1 rounded bg-ds-red-tint px-1.5 py-0.5 text-[10px] font-bold uppercase text-ds-red-ink">{t('medcom.bookingsvar.rettet_chip', 'Rettet')}</span>
                    )}
                    {row.corrected_by_id && (
                      <span className="ml-1 rounded bg-ds-neutral-tint px-1.5 py-0.5 text-[10px] font-medium text-ds-text">{t('medcom.bookingsvar.replaced_chip', 'Erstattet')}</span>
                    )}
                    {row.answers && <div className="text-xs text-ds-teal">{answersLabel(row.answers.letter_identifier, row.answers.created_at)}</div>}
                  </td>
                  <td className="py-1.5 pr-2">
                    {row.patient_display_name ?? row.patient_name ?? '–'}
                    {row.patient_id_kind === 'erstatnings_cpr' && (
                      <span className="ml-1 rounded bg-ds-amber-tint px-1.5 py-0.5 text-[10px] font-medium text-ds-amber-ink">Erstatnings-CPR</span>
                    )}
                    {!row.patient_id && <div className="text-xs text-ds-amber-ink">{t('medcom.bookingsvar.unmatched', 'Ikke knyttet til en patient')}</div>}
                  </td>
                  <td className="py-1.5 pr-2">{row.sender_name ?? row.sender_ean ?? '–'}</td>
                  <td className="py-1.5 pr-2 whitespace-nowrap font-medium">{bookingDateTime(row.confirmation_at) || '–'}</td>
                  <td className="py-1.5 pr-2 text-xs">{row.booking_text ?? ''}</td>
                  <td className="py-1.5 whitespace-nowrap">{formatTime(row.received_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {openId && (
        <div className="space-y-2">
          <div className="flex justify-end">
            <Button type="button" size="sm" variant="secondary" onClick={() => { setOpenId(null); setDetail(null); }}>
              {t('medcom.bookingsvar.close', 'Luk')}
            </Button>
          </div>
          {canAttach && openRow && !openRow.patient_id && <AttachPatient clinicId={clinicId} row={openRow} onDone={() => void load()} />}
          {detailBusy && <p className="text-sm text-ds-muted">{t('medcom.bookingsvar.loading', 'Henter bookingsvaret…')}</p>}
          {detailError && <p role="alert" className="text-sm text-ds-red-ink">{detailError}</p>}
          {detail && <ReceivedBookingsvarDetail clinicId={clinicId} detail={detail} onOpenExchange={(id) => void openDetail(id)} />}
        </div>
      )}
    </section>
  );
}
